// Email Service for handling notifications and newsletters
// This is a frontend service that communicates with the backend email API

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

class EmailService {
  // Send event notification emails
  static async sendEventNotification(eventData, recipients) {
    try {
      const response = await fetch(`${API_BASE_URL}/email/event-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          event: eventData,
          recipients: recipients
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send event notification');
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending event notification:', error);
      throw error;
    }
  }

  // Send registration status notification
  static async sendRegistrationNotification(registrationData, status, notes = '') {
    try {
      const response = await fetch(`${API_BASE_URL}/email/registration-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          registration: registrationData,
          status: status,
          notes: notes
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send registration notification');
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending registration notification:', error);
      throw error;
    }
  }

  // Send newsletter to subscribers
  static async sendNewsletter(newsletterData, subscriberGroups = ['all']) {
    try {
      const response = await fetch(`${API_BASE_URL}/email/newsletter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          newsletter: newsletterData,
          subscriberGroups: subscriberGroups
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send newsletter');
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending newsletter:', error);
      throw error;
    }
  }

  // Subscribe to email notifications
  static async subscribeToEmails(email, preferences = {}) {
    try {
      const response = await fetch(`${API_BASE_URL}/email/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          preferences: {
            events: preferences.events || true,
            news: preferences.news || true,
            registrations: preferences.registrations || false,
            newsletters: preferences.newsletters || true
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to subscribe to emails');
      }

      return await response.json();
    } catch (error) {
      console.error('Error subscribing to emails:', error);
      throw error;
    }
  }

  // Unsubscribe from email notifications
  static async unsubscribeFromEmails(email, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/email/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          token: token
        })
      });

      if (!response.ok) {
        throw new Error('Failed to unsubscribe from emails');
      }

      return await response.json();
    } catch (error) {
      console.error('Error unsubscribing from emails:', error);
      throw error;
    }
  }

  // Get email subscription status
  static async getSubscriptionStatus(email) {
    try {
      const response = await fetch(`${API_BASE_URL}/email/subscription-status?email=${encodeURIComponent(email)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get subscription status');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting subscription status:', error);
      throw error;
    }
  }

  // Send test email (admin only)
  static async sendTestEmail(emailData) {
    try {
      const response = await fetch(`${API_BASE_URL}/email/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(emailData)
      });

      if (!response.ok) {
        throw new Error('Failed to send test email');
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending test email:', error);
      throw error;
    }
  }

  // Get email templates
  static async getEmailTemplates() {
    try {
      const response = await fetch(`${API_BASE_URL}/email/templates`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get email templates');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting email templates:', error);
      throw error;
    }
  }
}

export default EmailService;

// Email template helpers
export const EmailTemplates = {
  // Event notification templates
  EVENT_CREATED: 'event_created',
  EVENT_UPDATED: 'event_updated',
  EVENT_CANCELLED: 'event_cancelled',
  EVENT_REMINDER: 'event_reminder',
  
  // Registration templates
  REGISTRATION_RECEIVED: 'registration_received',
  REGISTRATION_APPROVED: 'registration_approved',
  REGISTRATION_REJECTED: 'registration_rejected',
  REGISTRATION_REMINDER: 'registration_reminder',
  
  // Newsletter templates
  NEWSLETTER_WEEKLY: 'newsletter_weekly',
  NEWSLETTER_MONTHLY: 'newsletter_monthly',
  NEWSLETTER_SPECIAL: 'newsletter_special',
  
  // General templates
  WELCOME: 'welcome',
  SUBSCRIPTION_CONFIRMED: 'subscription_confirmed',
  UNSUBSCRIBE_CONFIRMED: 'unsubscribe_confirmed'
};

// Email notification preferences
export const NotificationTypes = {
  EVENTS: 'events',
  NEWS: 'news',
  REGISTRATIONS: 'registrations',
  NEWSLETTERS: 'newsletters',
  REMINDERS: 'reminders'
};